import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { WhatsappLogo, Trash, PaperPlaneTilt, Users } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATS = ["", "Farmhouses", "Resorts", "Banquet Halls", "Function Halls"];
const PRESETS = {
  "Weekend Offer": "Hi {name}! 🌿 This weekend get special prices on farmhouses & resorts around {location}. Reply to know more or book on Avinya Bookings.",
  "New Property": "Hi {name}! ✨ A beautiful new venue just got listed near {location} on Avinya Bookings. Want the details?",
  "Farmhouse Offer": "Hi {name}! 🏡 Planning a get-together? Book a farmhouse in {location} with easy online advance payment on Avinya Bookings.",
  "Resort Offer": "Hi {name}! 🏝️ Family stay coming up? Resorts near {location} are available on Avinya Bookings.",
  "Banquet Hall Offer": "Hi {name}! 💍 Wedding or reception soon? Reserve a banquet hall in {location} on Avinya Bookings.",
  "Booking Reminder": "Hi {name}! ⏳ Your date is filling up fast. Complete your booking on Avinya Bookings to lock your venue.",
  "Customer Follow-up": "Hi {name}! 👋 Just checking in — need help finding the right venue in {location}? We're happy to help on Avinya Bookings.",
};
const empty = { name: "", template: "", target_category: "", target_location: "", segment: "leads", status: "draft" };

export default function AdminWhatsApp() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState(empty);
  const [recips, setRecips] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const load = () => api.get("/admin/wa-campaigns").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.name || !f.template) return toast.error("Enter a campaign name and message.");
    try { await api.post("/admin/wa-campaigns", f); toast.success("Campaign saved"); setF(empty); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (c) => { await api.delete(`/admin/wa-campaigns/${c.id}`); load(); };
  const setStatus = async (c, status) => { await api.patch(`/admin/wa-campaigns/${c.id}`, { status }); load(); };
  const viewRecipients = async (c) => { setRecips({ loading: true, campaign: c }); const r = await api.get(`/admin/wa-campaigns/${c.id}/recipients`); setRecips({ ...r.data, campaign: c }); };
  const waHref = (phone, message) => `https://wa.me/${String(phone).replace(/[^0-9]/g, "")}?text=${encodeURIComponent(message)}`;

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">WhatsApp Campaigns</h1>
      <p className="mt-1 text-sm text-stone-500">Prepare message templates and reach out via click-to-WhatsApp. No bulk auto-send — you stay in control and respect consent.</p>

      <div className="mt-5 rounded-2xl border border-stone-200 bg-white p-6">
        <p className="mb-3 font-heading font-semibold">New campaign</p>
        <div className="mb-3 flex flex-wrap gap-2">
          {Object.keys(PRESETS).map((k) => <button key={k} data-testid={`preset-${k}`} onClick={() => setF({ ...f, name: f.name || k, template: PRESETS[k] })} className="rounded-full border border-stone-200 px-3 py-1 text-xs text-stone-600 hover:bg-stone-50">{k}</button>)}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div><Label className="text-xs text-stone-500">Campaign name</Label><Input data-testid="wa-name" value={f.name} onChange={set("name")} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs text-stone-500">Target location</Label><Input value={f.target_location} onChange={set("target_location")} className="mt-1 rounded-xl" placeholder="Moinabad" /></div>
          <div><Label className="text-xs text-stone-500">Target category</Label>
            <Select value={f.target_category || "all"} onValueChange={(v) => set("target_category")(v === "all" ? "" : v)}><SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="All" /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c || "all"} value={c || "all"}>{c || "All categories"}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs text-stone-500">Audience</Label>
            <Select value={f.segment} onValueChange={set("segment")}><SelectTrigger data-testid="wa-segment" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="leads">Enquiry leads</SelectItem><SelectItem value="customers">Registered customers</SelectItem></SelectContent></Select>
          </div>
        </div>
        <div className="mt-3"><Label className="text-xs text-stone-500">Message ({"{name}"} and {"{location}"} are auto-filled)</Label><Textarea data-testid="wa-template" value={f.template} onChange={set("template")} className="mt-1 min-h-[100px] rounded-xl" /></div>
        <Button data-testid="wa-create" onClick={create} className="mt-3 rounded-xl bg-primary hover:bg-green-900">Save campaign</Button>
      </div>

      <div className="mt-6 space-y-3">
        {items === null ? null : items.length === 0 ? (
          <EmptyState icon={WhatsappLogo} title="No campaigns yet" message="Pick a template above and save your first WhatsApp campaign." />
        ) : items.map((c) => (
          <div key={c.id} data-testid={`wa-${c.id}`} className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-heading font-semibold text-stone-900">{c.name}</p>
                <p className="mt-1 line-clamp-2 text-sm text-stone-500">{c.template}</p>
                <p className="mt-1 text-xs text-stone-400">{c.segment === "customers" ? "Registered customers" : "Enquiry leads"}{c.target_category ? ` · ${c.target_category}` : ""}{c.target_location ? ` · ${c.target_location}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={c.status} onValueChange={(v) => setStatus(c, v)}><SelectTrigger className="h-8 w-24 rounded-full text-xs"><SelectValue /></SelectTrigger><SelectContent>{["draft", "ready", "paused"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                <button data-testid={`wa-recipients-${c.id}`} onClick={() => viewRecipients(c)} className="flex items-center gap-1 rounded-full bg-green-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-600"><Users size={14} /> Recipients</button>
                <button onClick={() => del(c)} className="text-stone-400 hover:text-destructive"><Trash size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!recips} onOpenChange={(o) => !o && setRecips(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Recipients{recips?.count != null ? ` (${recips.count})` : ""}</DialogTitle></DialogHeader>
          {recips?.loading ? <p className="text-sm text-stone-400">Loading…</p> : recips?.recipients?.length ? (
            <div className="space-y-2">
              {recips.recipients.map((r, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-stone-200 p-3">
                  <div><p className="text-sm font-medium text-stone-800">{r.name || "—"}</p><p className="text-xs text-stone-400">{r.phone}</p></div>
                  <a data-testid={`wa-send-${i}`} href={waHref(r.phone, r.message)} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full bg-green-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-600"><PaperPlaneTilt size={14} /> WhatsApp</a>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-stone-400">No matching contacts. Try a broader audience or category.</p>}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
